/**
 * Management reports: overview (KPIs vs previous period + trend), operations,
 * customer insights and the earned-vs-collected money summary.
 */
import { Alert, Box, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import moment from 'moment';
import {
  useGetCustomersReportQuery,
  useGetMoneyReportQuery,
  useGetOperationsReportQuery,
  useGetOverviewReportQuery,
  type DurationStats,
  type OverviewKpis,
  type ReportPeriod,
} from '../../store/api/detailedReportsApi';
import { formatCurrency, formatNumber, formatPercent } from '../../utils/format';
import { StatusBreakdownCards } from './OrdersBreakdownCards';

// ------------------------------------------------------------------ shared

const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6">{title}</Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hint}
      </Typography>
    )}
    <Box sx={{ mt: 1.5 }}>{children}</Box>
  </Box>
);

const grid = (md: number) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${md}, 1fr)` },
  gap: 2,
});

const Tile = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: string;
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color }}>
        {value}
      </Typography>
      {sub && <Box sx={{ mt: 0.5 }}>{sub}</Box>}
    </CardContent>
  </Card>
);

/** Horizontal bars: label, bar sized to the biggest value, numbers on the side */
const BarList = ({
  items,
  emptyText,
}: {
  items: Array<{ key: string; label: string; value: number; extra?: string }>;
  emptyText: string;
}) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  const total = items.reduce((sum, i) => sum + i.value, 0);
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {items.map((item) => (
        <Box key={item.key}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {item.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {formatNumber(item.value)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatPercent(total ? (item.value * 100) / total : 0, 0)}
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(item.value * 100) / max}
            sx={{ height: 8, borderRadius: 4, my: 0.75, bgcolor: 'action.hover' }}
          />
          {item.extra && (
            <Typography variant="caption" color="text.secondary">
              {item.extra}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

const LoadingOrEmpty = ({ loading }: { loading: boolean }) => {
  const { t } = useTranslation();
  return (
    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
      {loading ? t('common.loading') : t('reports.detailed.noData')}
    </Typography>
  );
};

// ---------------------------------------------------------------- overview

type KpiKey = keyof OverviewKpis;

export const OverviewReport = ({ period }: { period: ReportPeriod }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, isFetching } = useGetOverviewReportQuery(period);
  const report = data?.data;
  if (!report) return <LoadingOrEmpty loading={isFetching} />;

  const { current, previous } = report;

  const delta = (key: KpiKey, kind: 'count' | 'money' | 'rate' | 'rating', lowerIsBetter = false) => {
    if (!previous) return null;
    const now = current[key] as number | null;
    const before = previous[key] as number | null;
    if (now === null || before === null) return null;
    const diff = now - before;
    if (diff === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          {t('reports.mgmt.noChange')}
        </Typography>
      );
    }
    const good = lowerIsBetter ? diff < 0 : diff > 0;
    const text =
      kind === 'rate'
        ? t('reports.mgmt.points', { value: Math.abs(diff).toFixed(1) })
        : kind === 'rating'
          ? Math.abs(diff).toFixed(1)
          : before
            ? `${Math.round((Math.abs(diff) * 100) / before)}%`
            : kind === 'money'
              ? formatCurrency(Math.abs(diff))
              : formatNumber(Math.abs(diff));
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: good ? 'success.main' : 'error.main' }}
      >
        {diff > 0 ? <UpIcon sx={{ fontSize: 16 }} /> : <DownIcon sx={{ fontSize: 16 }} />}
        <Typography variant="caption" fontWeight={600}>
          {text}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('reports.mgmt.vsPrevious')}
        </Typography>
      </Box>
    );
  };

  const kpis: Array<{ label: string; value: string; delta: React.ReactNode; color?: string }> = [
    { label: t('reports.mgmt.kpi.orders'), value: formatNumber(current.total_orders), delta: delta('total_orders', 'count') },
    {
      label: t('reports.mgmt.kpi.earned'),
      value: formatCurrency(current.completed_value),
      delta: delta('completed_value', 'money'),
      color: theme.palette.success.main,
    },
    {
      label: t('reports.mgmt.kpi.avgOrder'),
      value: current.avg_order_value === null ? '—' : formatCurrency(current.avg_order_value),
      delta: delta('avg_order_value', 'money'),
    },
    {
      label: t('reports.mgmt.kpi.completionRate'),
      value: current.completion_rate === null ? '—' : `${current.completion_rate}%`,
      delta: delta('completion_rate', 'rate'),
    },
    {
      label: t('reports.mgmt.kpi.cancellationRate'),
      value: current.cancellation_rate === null ? '—' : `${current.cancellation_rate}%`,
      delta: delta('cancellation_rate', 'rate', true),
    },
    { label: t('reports.mgmt.kpi.customers'), value: formatNumber(current.customers), delta: delta('customers', 'count') },
    { label: t('reports.mgmt.kpi.newCustomers'), value: formatNumber(current.new_customers), delta: delta('new_customers', 'count') },
    {
      label: t('reports.mgmt.kpi.rating'),
      value: current.avg_service_rating === null ? '—' : `★ ${current.avg_service_rating.toFixed(1)}`,
      delta: delta('avg_service_rating', 'rating'),
    },
  ];

  const bucketLabel = (value: string) =>
    report.bucket === 'month' ? moment(value).format('MMM YYYY') : moment(value).format('DD/MM');

  return (
    <CardContent>
      <Section
        title={t('reports.mgmt.overview.title')}
        hint={
          report.previous_period
            ? t('reports.mgmt.overview.comparedTo', report.previous_period)
            : t('reports.mgmt.overview.noComparison')
        }
      >
        <Box sx={grid(4)}>
          {kpis.map((k) => (
            <Tile key={k.label} label={k.label} value={k.value} sub={k.delta} color={k.color} />
          ))}
        </Box>
      </Section>

      <Section title={t('reports.mgmt.overview.trend')} hint={t(`reports.mgmt.overview.bucket.${report.bucket}`)}>
        <Card>
          <CardContent sx={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={report.trend} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="bucket" tickFormatter={bucketLabel} fontSize={12} />
                <YAxis yAxisId="orders" allowDecimals={false} fontSize={12} />
                <YAxis yAxisId="money" orientation="right" fontSize={12} />
                <Tooltip
                  labelFormatter={(v) => bucketLabel(String(v))}
                  formatter={(value, name) =>
                    name === t('reports.mgmt.overview.earned') ? formatCurrency(Number(value)) : formatNumber(Number(value))
                  }
                />
                <Legend />
                <Bar isAnimationActive={false} yAxisId="orders" dataKey="orders" name={t('reports.mgmt.overview.orders')} fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                <Bar isAnimationActive={false} yAxisId="orders" dataKey="cancelled" name={t('reports.mgmt.overview.cancelled')} fill={theme.palette.error.light} radius={[4, 4, 0, 0]} />
                <Line isAnimationActive={false}
                  yAxisId="money"
                  dataKey="completed_value"
                  name={t('reports.mgmt.overview.earned')}
                  stroke={theme.palette.success.main}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Section>

      <StatusBreakdownCards items={report.status_breakdown} />
    </CardContent>
  );
};

// -------------------------------------------------------------- operations

const formatHours = (hours: number | null, t: TFunction) => {
  if (hours === null) return '—';
  if (hours < 1) return t('reports.mgmt.minutes', { value: Math.round(hours * 60) });
  if (hours < 48) return t('reports.mgmt.hours', { value: Number(hours.toFixed(1)) });
  return t('reports.mgmt.days', { value: Number((hours / 24).toFixed(1)) });
};

const DurationCard = ({ title, stats }: { title: string; stats: DurationStats }) => {
  const { t } = useTranslation();
  const buckets = (['under_1h', '1_4h', '4_24h', 'over_24h'] as const).map((key) => ({
    key,
    label: t(`reports.mgmt.duration.${key}`),
    value: stats.distribution[key],
  }));
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, my: 1.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('reports.mgmt.average')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatHours(stats.avg_hours, t)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('reports.mgmt.median')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatHours(stats.median_hours, t)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('reports.mgmt.measuredOrders')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(stats.count)}
            </Typography>
          </Box>
        </Box>
        <BarList items={buckets} emptyText={t('reports.detailed.noData')} />
      </CardContent>
    </Card>
  );
};

// Week as shown in the UI (Saturday first); values follow the backend (0 = Monday)
const WEEK_ORDER = [5, 6, 0, 1, 2, 3, 4];

export const OperationsReport = ({ period }: { period: ReportPeriod }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, isFetching } = useGetOperationsReportQuery(period);
  const report = data?.data;
  if (!report) return <LoadingOrEmpty loading={isFetching} />;

  const { cancellations } = report;
  const weekdays = WEEK_ORDER.map((d) => ({
    day: t(`providers.days.${d}`),
    orders: report.by_weekday.find((w) => w.weekday === d)?.orders ?? 0,
  }));
  const hours = report.by_hour.map((h) => ({ hour: `${String(h.hour).padStart(2, '0')}:00`, orders: h.orders }));

  return (
    <CardContent>
      <Section title={t('reports.mgmt.ops.responseTitle')} hint={t('reports.mgmt.ops.responseHint')}>
        {report.orders_without_timeline > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('reports.mgmt.ops.untracked', { count: report.orders_without_timeline })}
          </Alert>
        )}
        <Box sx={grid(2)}>
          <DurationCard title={t('reports.mgmt.ops.timeToAccept')} stats={report.time_to_accept} />
          <DurationCard title={t('reports.mgmt.ops.timeToComplete')} stats={report.time_to_complete} />
        </Box>
      </Section>

      <Section title={t('reports.mgmt.ops.peakTitle')} hint={t('reports.mgmt.ops.peakHint')}>
        <Box sx={grid(2)}>
          {[
            { title: t('reports.mgmt.ops.byWeekday'), data: weekdays, key: 'day' },
            { title: t('reports.mgmt.ops.byHour'), data: hours, key: 'hour' },
          ].map((chart) => (
            <Card key={chart.key}>
              <CardContent sx={{ height: 280 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {chart.title}
                </Typography>
                <ResponsiveContainer width="100%" height="88%">
                  <BarChart data={chart.data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey={chart.key} fontSize={11} interval={chart.key === 'hour' ? 2 : 0} />
                    <YAxis allowDecimals={false} fontSize={11} width={30} />
                    <Tooltip formatter={(v) => [formatNumber(Number(v)), t('reports.mgmt.overview.orders')]} />
                    <Bar isAnimationActive={false} dataKey="orders" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Section>

      <Box sx={grid(2)}>
        <Section
          title={t('reports.mgmt.ops.cancellationsTitle')}
          hint={t('reports.mgmt.ops.cancellationsHint', {
            count: cancellations.total,
            rate: cancellations.rate ?? 0,
          })}
        >
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {Object.entries(cancellations.by_source).map(([source, count]) => (
                  <Chip key={source} size="small" label={`${t(`orders.timeline.source.${source}`)}: ${count}`} />
                ))}
              </Box>
              <BarList
                items={cancellations.reasons.map((r) => ({
                  key: r.reason,
                  label: r.reason === '—' ? t('reports.mgmt.ops.noReason') : r.reason,
                  value: r.count,
                }))}
                emptyText={t('reports.mgmt.ops.noCancellations')}
              />
            </CardContent>
          </Card>
        </Section>

        <Section title={t('reports.mgmt.ops.areasTitle')} hint={t('reports.mgmt.ops.areasHint')}>
          <Card>
            <CardContent>
              <BarList
                items={report.areas.map((a) => ({
                  key: a.area,
                  label: a.area === '—' ? t('reports.mgmt.ops.noArea') : a.area,
                  value: a.orders,
                  extra: formatCurrency(a.completed_value),
                }))}
                emptyText={t('reports.detailed.noData')}
              />
            </CardContent>
          </Card>
        </Section>
      </Box>
    </CardContent>
  );
};

// --------------------------------------------------------------- customers

export const CustomersInsights = ({ period }: { period: ReportPeriod }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, isFetching } = useGetCustomersReportQuery(period);
  const report = data?.data;
  if (!report) return <LoadingOrEmpty loading={isFetching} />;

  const leadLabel = (source: string) =>
    source === 'UNKNOWN' ? t('reports.mgmt.customers.unknownSource') : t(`users.leadSources.${source}`);
  const genderLabel = (g: string) =>
    g === 'M' ? t('users.gender.M') : g === 'F' ? t('users.gender.F') : t('reports.mgmt.customers.unknown');
  const genderColor = (g: string) =>
    g === 'F' ? theme.palette.secondary.main : g === 'M' ? theme.palette.primary.main : theme.palette.grey[400];
  const genderTotal = report.genders.reduce((sum, g) => sum + g.orders, 0);

  return (
    <CardContent>
      <Section title={t('reports.mgmt.customers.title')}>
        <Box sx={grid(4)}>
          <Tile label={t('reports.mgmt.customers.customers')} value={formatNumber(report.customers)} />
          <Tile
            label={t('reports.mgmt.customers.repeat')}
            value={formatNumber(report.repeat_customers)}
            sub={
              <Typography variant="caption" color="text.secondary">
                {t('reports.mgmt.customers.repeatRate', { rate: report.repeat_rate ?? 0 })}
              </Typography>
            }
          />
          <Tile label={t('reports.mgmt.customers.newAccounts')} value={formatNumber(report.new_accounts)} />
          <Tile
            label={t('reports.mgmt.customers.returning')}
            value={formatNumber(report.new_vs_returning.returning.customers)}
            sub={
              <Typography variant="caption" color="text.secondary">
                {t('reports.mgmt.customers.returningSub', {
                  orders: report.new_vs_returning.returning.orders,
                  value: formatCurrency(report.new_vs_returning.returning.completed_value),
                })}
              </Typography>
            }
          />
        </Box>
        {!report.has_comparison_base && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('reports.mgmt.customers.needStartDate')}
          </Typography>
        )}
      </Section>

      <Box sx={grid(2)}>
        <Section title={t('reports.mgmt.customers.leadTitle')} hint={t('reports.mgmt.customers.leadHint')}>
          <Card>
            <CardContent>
              <BarList
                items={report.lead_sources.map((l) => ({
                  key: l.lead_source,
                  label: leadLabel(l.lead_source),
                  value: l.orders,
                  extra: `${t('reports.mgmt.customers.patientsCount', { count: l.patients })} · ${formatCurrency(l.completed_value)}`,
                }))}
                emptyText={t('reports.detailed.noData')}
              />
            </CardContent>
          </Card>
        </Section>

        <Section title={t('reports.mgmt.customers.patientsTitle')} hint={t('reports.mgmt.customers.patientsHint')}>
          <Card>
            <CardContent>
              {/* Charts stay LTR so the axis labels don't collide under RTL */}
              <Box dir="ltr" sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={report.age_groups.map((g) => ({ ...g, label: t(`reports.mgmt.ageGroups.${g.group}`) }))}
                    margin={{ top: 20, right: 8, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                    <XAxis dataKey="label" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis hide allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: theme.palette.action.hover }}
                      formatter={(v) => [formatNumber(Number(v)), t('reports.mgmt.overview.orders')]}
                    />
                    <Bar
                      isAnimationActive={false}
                      dataKey="orders"
                      fill={theme.palette.primary.main}
                      radius={[6, 6, 0, 0]}
                      maxBarSize={56}
                    >
                      <LabelList dataKey="orders" position="top" fontSize={12} fill={theme.palette.text.secondary} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              {genderTotal > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.hover' }}>
                    {report.genders.map((g) => (
                      <Box
                        key={g.gender}
                        sx={{ width: `${(g.orders * 100) / genderTotal}%`, bgcolor: genderColor(g.gender) }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    {report.genders.map((g) => (
                      <Box key={g.gender} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: genderColor(g.gender) }} />
                        <Typography variant="body2">
                          {genderLabel(g.gender)}{' '}
                          <Typography component="span" variant="body2" fontWeight={700}>
                            {formatNumber(g.orders)}
                          </Typography>{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({formatPercent((g.orders * 100) / genderTotal, 0)})
                          </Typography>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Section>
      </Box>
    </CardContent>
  );
};

// ------------------------------------------------------------------- money

export const MoneySummary = ({ period }: { period: ReportPeriod }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data } = useGetMoneyReportQuery(period);
  const report = data?.data;
  if (!report) return null;

  const collectedPct = report.earned ? Math.round((report.collected * 100) / report.earned) : 0;
  return (
    <Section title={t('reports.mgmt.money.title')} hint={t('reports.mgmt.money.hint')}>
      <Box sx={grid(4)}>
        <Tile label={t('reports.mgmt.money.earned')} value={formatCurrency(report.earned)} color={theme.palette.success.main} />
        <Tile
          label={t('reports.mgmt.money.collected')}
          value={formatCurrency(report.collected)}
          sub={
            <Box>
              <LinearProgress variant="determinate" value={collectedPct} color="success" sx={{ height: 6, borderRadius: 3 }} />
              <Typography variant="caption" color="text.secondary">
                {t('reports.mgmt.money.collectedPct', { percent: collectedPct, count: report.collected_orders })}
              </Typography>
            </Box>
          }
        />
        <Tile
          label={t('reports.mgmt.money.uncollected')}
          value={formatCurrency(report.uncollected)}
          color={report.uncollected ? theme.palette.warning.main : undefined}
          sub={
            <Typography variant="caption" color="text.secondary">
              {t('reports.mgmt.money.uncollectedOrders', { count: report.uncollected_orders })}
            </Typography>
          }
        />
        <Tile
          label={t('reports.mgmt.money.fees')}
          value={formatCurrency(report.delivery_fees)}
          sub={
            <Typography variant="caption" color="text.secondary">
              {t('reports.mgmt.money.serviceValue', { value: formatCurrency(report.service_value) })}
            </Typography>
          }
        />
      </Box>
    </Section>
  );
};
